/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { extractNumberRange } from 'utils/reservations/reservationTableUtils'
import { toSearchFilterList } from 'utils/utility'

export interface OfferSearchFilterEntry {
    id: string
    value: unknown
}

const OFFERS_SEARCH_FIELD_MAP: Record<string, string[]> = {
    company_branche: ['company.business_category'],
    company_state: ['company.address.region'],
    contact_gender: ['contact.gender'],
    company_address: ['company.address.street_name', 'company.address.postal_code', 'company.address.town'],
    contact_position: ['contact.position'],
}

const OFFERS_SCALAR_SELECT_FILTER_IDS = new Set([
    'company.type',
    'status',
    'seller_id',
    'package',
    'contact_position',
    'company_branche',
    'contact_gender',
])

const OFFERS_NUMBER_RANGE_FILTER_IDS = new Set(['persons', 'value'])

const normalizeRangeFilterValue = (id: string, value: unknown): unknown => {
    if (!OFFERS_NUMBER_RANGE_FILTER_IDS.has(id)) {
        return value
    }

    const parser = id === 'value' ? parseFloat : (item: string) => parseInt(item, 10)
    const normalizedRange = extractNumberRange(value, parser)
    return normalizedRange ?? undefined
}

const normalizeSelectFilterValue = (id: string, value: unknown): unknown => {
    if (!OFFERS_SCALAR_SELECT_FILTER_IDS.has(id) || !Array.isArray(value)) {
        return value
    }

    if (value.length === 0) {
        return undefined
    }

    if (value.length === 1) {
        return value[0]
    }

    return value
}

const normalizeOfferSearchFilter = (
    filter: OfferSearchFilterEntry,
    sourceFilterId: string = filter.id
): OfferSearchFilterEntry[] => {
    const selectValue = normalizeSelectFilterValue(sourceFilterId, filter.value)
    if (selectValue === undefined) {
        return []
    }

    const normalizedValue = normalizeRangeFilterValue(filter.id, selectValue)
    if (normalizedValue === undefined) {
        return []
    }

    return [{ id: filter.id, value: normalizedValue }]
}

export const transformOffersSearch = (
    filters: OfferSearchFilterEntry[] | OfferSearchFilterEntry | undefined
): OfferSearchFilterEntry[] => {
    return toSearchFilterList(filters).flatMap((filter) => {
        if (!filter?.id) {
            return []
        }

        const mappedIds = OFFERS_SEARCH_FIELD_MAP[filter.id] ?? [filter.id]

        return mappedIds.flatMap((id) => normalizeOfferSearchFilter({ id, value: filter.value }, filter.id))
    })
}
